import { useCallback, useMemo, useState } from "react";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Button, SearchInput } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { useRecentAnalysis } from "app/custom-hooks/react-query/useRecentAnalysis";
import { translate } from "app/filters/i18nFilter";
import { AccountBalanceBanner } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/AccountBalanceBanner";
import { LLMTable } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMTable/LLMTable";
import { TableFilters } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/TableFilters/TableFilters";
import { LLMEmptyStateHomepage } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMEmptyStateHomepage";
import { LLMSetupModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMSetup";
import { HomepageContainer } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/styledComponents";
import type {
    DropdownOnClickProps,
    IAiTrackerTableRow,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { MenuItemType, TabFilter } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { doesCampaignMatchFilter } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/utils";
import { useDeleteCampaignModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/delete/useDeleteCampaignModal";
import { useEditCampaignModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/useEditCampaignModal";
import { useSubscribeMenuItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/subscribe/useSubscribeMenuItem";
import { LLMDomainAutocomplete } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMDomainAutocomplete";
import {
    getDefaultModel,
    getMaxCampaigns,
    hasNonDemoCampaigns as hasNonDemoCampaignsUtil,
    isCampaignCompleted,
    isCampaignFailed,
    navigateToReport,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { isWeeklyReportSubscribed } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { AssetPermissions } from "app/services/asset-permissions/AssetPermissions";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";
import { getHooksVersion } from "app/UtilitiesAndConstants/UtilityFunctions/hooksUtils";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";
import { PageLoader } from "pro-features/components/Loaders/PageLoader";

import { useLLMTrackerCampaigns } from "../shared/llm-tracker-service/useLLMTrackerCampaigns";
import { isAeoSolution } from "app/common/utils/isAeoSolution";
import { MarketingModals } from "app/components/modals/marketing-modals/MarketingModals";
import { PageTopBanner } from "app/components/PageTopBanner/PageTopBanner";

export const LLMHomepage = () => {
    const { data: campaigns = [], isLoading: areCampaignsLoading } = useLLMTrackerCampaigns();
    const hasNonDemoCampaigns = hasNonDemoCampaignsUtil(campaigns);
    const [selectedDomain, setSelectedDomain] = useState<IWebsiteItem | null>(null);
    const [isCreationModalOpen, setCreationModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [tabIndex, setTabIndex] = useState<number>(TabFilter.ALL);
    const personalCampaignsCount = useMemo<number>(() => {
        const isInternalUser = swSettings.user.isInternalUser;

        return campaigns.filter((campaign) => {
            return !campaign.isDemo && (!isInternalUser || new AssetPermissions(campaign).isOwner);
        }).length;
    }, [campaigns]);

    const isAeoPackage = isAeoSolution(swSettings);

    const isCampaignLimitReached = personalCampaignsCount >= getMaxCampaigns();
    const { data: recentItems, isLoading: isFetching } = useRecentAnalysis();
    const { modal: deleteModal, openModal: openDeleteModal } = useDeleteCampaignModal();
    const { modal: editModal, openModal: openEditModal } = useEditCampaignModal();
    const { handleSubscribe, isPending: isSubscribePending } = useSubscribeMenuItem();
    const maxCampaigns = getMaxCampaigns();
    const isCampaignsCountClaimValid = maxCampaigns !== null && maxCampaigns > 0;
    const tableData = useMemo<IAiTrackerTableRow[]>(() => {
        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const searchRegex = RegExp(escapedTerm, "i");
        return campaigns.reduce<IAiTrackerTableRow[]>((rows, campaign) => {
            const permissions = new AssetPermissions(campaign);

            if (
                doesCampaignMatchFilter(campaign, tabIndex, permissions) &&
                (searchRegex.test(campaign.name) || searchRegex.test(campaign.ownerUserName))
            ) {
                rows.push({
                    ...campaign,
                    isCompleted: isCampaignCompleted(campaign),
                    isFailed: isCampaignFailed(campaign),
                    topicCount: campaign.topics?.length ?? 0,
                    canEdit: permissions.canEdit,
                    canDelete: permissions.canDelete,
                    isOwner: permissions.isOwner,
                    languageName: campaign.language?.name ?? "",
                    isLocked: (!isCampaignsCountClaimValid || !getDefaultModel(campaign)) && !campaign.isDemo,
                });
            }

            return rows;
        }, []);
    }, [campaigns, searchTerm, tabIndex, isCampaignsCountClaimValid]);

    const openHook = (version: EHooksVersions = EHooksVersions.V5): void => {
        onHookClick("AiTracker", "digitalsuite_llm_solutions_homepage", version);
    };

    const createCampaign = (): void => {
        if (isCampaignLimitReached) {
            openHook(getHooksVersion(swSettings));
        } else {
            allTrackers.dispatchTrackEvent("Button", "click", "Get Started/Homepage");
            setCreationModalOpen(true);
        }
    };

    const onCampaignSelected = (row: IAiTrackerTableRow): void => {
        const campaignId = row.id;
        const campaign = campaigns.find(({ id }) => id === campaignId);

        if (campaign) {
            if (!isCampaignsCountClaimValid && !campaign.isDemo) {
                openHook(getHooksVersion(swSettings));
            } else {
                try {
                    navigateToReport(campaign);
                    allTrackers.dispatchTrackEvent("Button", "click", `Homepage/${campaign.name}`);
                } catch (error) {
                    Sentry.errorTracker({
                        e: error,
                        message: "Unable to navigate to campaign",
                        level: "error",
                        params: {
                            extra: {
                                campaignId,
                            },
                        },
                    });
                }
            }
        }
    };

    const onSearchChange = (text: string): void => {
        const searchValue = text?.trim() ?? "";

        if (searchValue) {
            allTrackers.dispatchTrackEvent("Search Bar", "Type", "Search");
        }

        setSearchTerm(searchValue);
    };

    const onDropdownClick = ({ id }: DropdownOnClickProps, row: IAiTrackerTableRow): void => {
        const campaign = campaigns.find(({ id }) => id === row.id);
        if (!campaign) {
            return;
        }

        const permissions = new AssetPermissions(campaign);

        switch (id) {
            case MenuItemType.Edit:
                allTrackers.dispatchTrackEvent("Drop Down", "Click", "Edit");
                openEditModal(row, !permissions.canEdit);
                break;

            case MenuItemType.Subscribe:
                handleSubscribe(row, !isWeeklyReportSubscribed(row.subscriptions));
                break;

            case MenuItemType.Delete:
                allTrackers.dispatchTrackEvent("Drop Down", "Click", "Delete");
                openDeleteModal(row);
                break;

            default:
                Sentry.error(`Unknown dropdown item id: ${id}`, null);
        }
    };

    const onCampaignCreatedNavigation = useCallback((campaign: IAiTrackerCampaign) => {
        navigateToReport(campaign);
    }, []);

    const onDemoCampaignClick = (campaign: IAiTrackerCampaign) => {
        try {
            navigateToReport(campaign);
            allTrackers.dispatchTrackEvent("Button", "click", `Demo Campaign/Homepage/${campaign.name}`);
        } catch (error) {
            Sentry.errorTracker({
                e: error,
                message: "Unable to navigate to demo campaign",
            });
        }
    };

    return (
        <Box
            className="main-homepage-container"
            overflow="auto"
            height="100%"
        >
            <PageLoader isLoading={areCampaignsLoading} />
            {isAeoPackage ? (
                <>
                    <PageTopBanner />
                    <MarketingModals />
                </>
            ) : null}
            {hasNonDemoCampaigns ? (
                <HomepageContainer>
                    <Flex
                        flexDirection="column"
                        maxWidth="1366px"
                        margin="0 auto"
                        gap="24px"
                    >
                        <>
                            <Flex
                                flexDirection="column"
                                gap="8px"
                            >
                                <Flex
                                    gap="8px"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Text
                                        as="h4"
                                        fontVariant="h4"
                                        color={colorsPalettes.carbon[500]}
                                        data-automation="ai-tracker-title"
                                    >
                                        {translate("llm.homepage.title")}
                                    </Text>
                                </Flex>
                                <AccountBalanceBanner
                                    campaignsCount={personalCampaignsCount}
                                    onHookClick={openHook}
                                />
                            </Flex>
                            <Flex
                                alignItems="flex-end"
                                margin="24px 0"
                                gap="8px"
                            >
                                <Flex
                                    flexDirection="column"
                                    gap="8px"
                                >
                                    <Text
                                        fontVariant="report"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {translate("llm.homepage.campaign_creation.label")}
                                    </Text>
                                    <LLMDomainAutocomplete
                                        selectedDomain={selectedDomain}
                                        setSelectedDomain={setSelectedDomain}
                                        recentItems={recentItems}
                                        isFetching={isFetching}
                                        placeholder={translate("llm.homepage.autocomplete.placeholder")}
                                        displaySelectedDomainIcon
                                        width="430px"
                                        height="40px"
                                    />
                                </Flex>
                                <Button
                                    type="primary"
                                    onClick={createCampaign}
                                    isDisabled={!selectedDomain}
                                    data-automation="llm-homepage-ai-button"
                                >
                                    {translate("llm.homepage.campaign_creation.cta")}
                                </Button>
                            </Flex>
                            <Stack gap={campaigns.length ? "8px" : "100px"}>
                                <Flex
                                    justifyContent="space-between"
                                    padding="0 10px 0 0"
                                >
                                    <TableFilters
                                        tabIndex={tabIndex}
                                        onSwitch={setTabIndex}
                                    />
                                    <SearchInput
                                        placeholder={translate("llm.homepage.table.search.placeholder")}
                                        data-automation-name="ai-tracker-search"
                                        onChange={onSearchChange}
                                        debounce={300}
                                    />
                                </Flex>
                                <LLMTable
                                    data={tableData}
                                    onDropdownClick={onDropdownClick}
                                    onCampaignSelection={onCampaignSelected}
                                    onLockedCampaignSelection={openHook}
                                    isSubscribePending={isSubscribePending}
                                />
                            </Stack>
                        </>
                    </Flex>
                </HomepageContainer>
            ) : (
                <LLMEmptyStateHomepage
                    campaigns={campaigns}
                    selectedDomain={selectedDomain}
                    onDomainChange={setSelectedDomain}
                    onCreateCampaign={createCampaign}
                    onDemoCampaignClick={onDemoCampaignClick}
                    isCreationAllowed={!isCampaignLimitReached}
                    title={translate("llm.homepage.title")}
                    subtitle={translate("llm.homepage.subtitle")}
                    autocompleteTitle={translate("llm.homepage.autocomplete.title")}
                />
            )}
            {editModal}
            {deleteModal}
            <LLMSetupModal
                isOpen={isCreationModalOpen}
                setIsOpen={setCreationModalOpen}
                domain={selectedDomain}
                onSuccessCallback={setSelectedDomain}
                onCampaignCreatedNavigation={onCampaignCreatedNavigation}
            />
        </Box>
    );
};
